import React, { type ChangeEvent, useCallback, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Checkbox as RoccoCheckbox } from '@rocco/components/checkbox';
import { Label as RoccoLabel } from '@rocco/components/label';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { FilterConfigModalItemSearchable } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { SearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/searchable-list-filter-state';
import type { SingleValueFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/single-value-filter-state';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import type { FilterModalElementProps } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';
import { DisplayMode } from '@rocco/types/enum/display-mode';
import { FilterGroupOptions } from '@rocco/types/filter-group-options';
import { useControllableValue, useDebounceFn } from 'ahooks';
import { Input as RoccoInput } from '@rocco/components/input';
import { isSearchableListFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';

export const FiltersModalSearchableList = (
    props: FilterModalElementProps<
        FilterConfigModalItemSearchable,
        SearchableListFilterState
    > & {
        onAlphabeticalGrouping: (args: AggregationArgs) => void;
    },
) => {
    const {
        filterConfigItem,
        filterState,
        onFilterStateUpdate,
        onAlphabeticalGrouping,
        displayMode,
    } = props;

    const { searchFilters } = useFiltersContext();
    const { handleAggregate } = searchFilters;

    const defaultShowCount = 60;
    const [showCount, setShowCount] = useState(defaultShowCount);
    const [isLoading, setIsLoading] = useState(false);

    const filterKey = filterConfigItem.filterKey;

    /** Gets updated state from aggregation, saving properties chosen by user **/
    const getUpdatedState = (state: SearchableListFilterState) => {
        const items = state.items.map((item) => {
            const selectedItem = state.selectedItems.find(
                (selectedItem) => selectedItem.value === item.value,
            );

            return selectedItem ? { ...selectedItem } : { ...item };
        });

        return { ...state, items };
    };

    const { run: searchAggregateRun } = useDebounceFn(
        (searchQuery: string) => {
            handleAggregate({
                aggregateType: 'searchListSearch',
                aggregateFilter: filterKey,
                aggregationsParams: {
                    [filterConfigItem.aggregationProperty]: {
                        search: searchQuery,
                        after: filterState.after ?? undefined, // Search service wants `undefined`, not null
                        size: filterState.size,
                    },
                },
                onStart: () => setIsLoading(true),
                onFinish: () => setIsLoading(false),
                onSuccess: (data) => {
                    const newListState = data.states?.[filterKey];

                    if (
                        newListState &&
                        isSearchableListFilterState(newListState)
                    ) {
                        const newItems = newListState?.items ?? [];

                        onFilterStateUpdate(
                            getUpdatedState({
                                ...filterState,
                                search: searchQuery,
                                items: newItems,
                                after: newListState?.after ?? null,
                                hasMoreItems: !!newListState?.items.length,
                                isSearching: false,
                            }),
                            false,
                        );
                    }
                },
            });
        },
        { wait: 500 },
    );

    const searchGroupAlphabetically = useCallback(
        (groupBy: keyof FilterGroupOptions | undefined) => {
            onAlphabeticalGrouping({
                aggregateType: groupBy
                    ? 'groupAlphabetical'
                    : 'searchListLoadMore',
                aggregateFilter: filterKey,
                aggregationsParams: {
                    [filterConfigItem.aggregationProperty]: {
                        after: undefined,
                        size: 1000,
                        startsWith: groupBy,
                    },
                },
                onStart: () => setIsLoading(true),
                onFinish: () => setIsLoading(false),
                onSuccess: (data) => {
                    const newListState = data.states?.[filterKey];

                    if (
                        newListState &&
                        isSearchableListFilterState(newListState)
                    ) {
                        setShowCount(defaultShowCount);
                        onFilterStateUpdate(
                            getUpdatedState({
                                ...filterState,
                                search: '',
                                items: newListState?.items ?? [],
                                groupedBy: groupBy,
                            }),
                            false,
                        );
                    }
                },
            });
        },
        [
            filterConfigItem.aggregationProperty,
            filterKey,
            filterState,
            onAlphabeticalGrouping,
            onFilterStateUpdate,
        ],
    );

    const showMoreCount = filterState.items.length - showCount;
    const showItems = filterState.items.slice(0, showCount);

    const columns =
        displayMode === DisplayMode.Desktop &&
        filterConfigItem.columns &&
        filterConfigItem.columns > 0
            ? filterConfigItem.columns
            : 1;

    const containerStyles = {
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gridTemplateRows: `repeat(${Math.ceil(showItems.length / columns)}, auto)`,
    };

    return (
        <div className="pt-2">
            <SearchableList
                isLoading={isLoading}
                searchInputPlaceholder={filterConfigItem.searchInputPlaceholder}
                searchQuery={filterState.search}
                groupAlphabetically={
                    filterConfigItem.allowAlphabeticalGrouping ?? false
                }
                groupedBy={filterState.groupedBy}
                firstCharacterGroups={filterState.firstCharacterGroups}
                onGroupingChange={searchGroupAlphabetically}
                onSearchQueryChange={(updatedSearchQuery) => {
                    onFilterStateUpdate(
                        {
                            ...filterState,
                            search: updatedSearchQuery,
                            groupedBy: undefined,
                            isSearching: true,
                        },
                        false,
                    );

                    searchAggregateRun(updatedSearchQuery);
                }}
            >
                {filterState.selectedItems.length > 0 && (
                    <div className="mb-10 flex flex-wrap gap-3">
                        {filterState.selectedItems.map((item, index) => (
                            <FiltersModalSearchableListItem
                                key={index}
                                item={item}
                                filterState={filterState}
                                onFilterStateUpdate={onFilterStateUpdate}
                                isSelected={true}
                            />
                        ))}
                    </div>
                )}

                <div
                    className="grid grid-flow-col-dense gap-2"
                    style={containerStyles}
                >
                    {showItems.map((item, index) => (
                        <FiltersModalSearchableListItem
                            key={index}
                            item={item}
                            filterState={filterState}
                            onFilterStateUpdate={onFilterStateUpdate}
                        />
                    ))}
                </div>

                {showMoreCount > 0 && (
                    <div
                        className="mt-10 flex cursor-pointer flex-col items-center"
                        onClick={() => {
                            setShowCount(showCount + defaultShowCount);
                        }}
                    >
                        <span className="font-medium text-gray-900">
                            Display{' '}
                            {showMoreCount < defaultShowCount
                                ? showMoreCount
                                : defaultShowCount}{' '}
                            more
                        </span>
                        <ChevronDown className="size-4.5" />
                    </div>
                )}
            </SearchableList>
        </div>
    );
};

interface FiltersModalSearchableListItemProps {
    item: SingleValueFilterState;
    filterState: SearchableListFilterState;
    isSelected?: boolean;
    onFilterStateUpdate: (
        state: SearchableListFilterState,
        shouldAggregate?: boolean,
    ) => void;
}

const FiltersModalSearchableListItem = (
    props: FiltersModalSearchableListItemProps,
) => {
    const {
        item,
        filterState,
        onFilterStateUpdate,
        isSelected = false,
    } = props;
    const {
        searchFilters: { handleToggleSearchableList },
    } = useFiltersContext();

    const id = `${item.value}${isSelected ? '-selected' : ''}`;

    return (
        <div
            className={cn(
                'flex items-center',
                isSelected
                    ? 'rounded-2 border border-gray-900 px-3 py-2'
                    : 'flex-row-reverse justify-between',
            )}
        >
            <RoccoCheckbox
                id={id}
                checked={item.checked}
                onCheckedChange={(checked) => {
                    if (handleToggleSearchableList) {
                        onFilterStateUpdate(
                            handleToggleSearchableList(
                                filterState,
                                item,
                                !!checked,
                            ),
                        );
                    }
                }}
            />
            <RoccoLabel
                htmlFor={id}
                target={isSelected ? 'checkbox' : 'checkboxReversed'}
                singleLine
            >
                {item.label}
            </RoccoLabel>
        </div>
    );
};

interface SearchableListProps extends React.PropsWithChildren {
    isLoading: boolean;
    groupAlphabetically: boolean;
    searchInputPlaceholder: string;
    searchQuery?: string;
    onSearchQueryChange?: (searchQuery: string) => void;
    groupedBy?: keyof FilterGroupOptions;
    onGroupingChange?: (groupBy?: keyof FilterGroupOptions) => void;
    firstCharacterGroups?: Partial<Record<keyof FilterGroupOptions, number>>;
}

const SearchableList = (props: SearchableListProps) => {
    const {
        children,
        isLoading,
        searchInputPlaceholder,
        groupedBy,
        onGroupingChange,
        groupAlphabetically,
        firstCharacterGroups,
    } = props;

    const [state, setState] = useControllableValue<string>(props, {
        defaultValue: '',
        valuePropName: 'searchQuery',
        trigger: 'onSearchQueryChange',
    });

    return (
        <div className={isLoading ? 'opacity-50' : undefined}>
            <div className="flex flex-col items-center justify-center gap-4 md:justify-between">
                <div className="w-full grow">
                    <RoccoInput
                        color="outline"
                        text="small"
                        shape="rounded"
                        placeholder={searchInputPlaceholder}
                        value={state}
                        showClearButton={true}
                        postfix={<Search className="size-4.5" />}
                        onChange={(evt: ChangeEvent<HTMLInputElement>) => {
                            setState(evt.target.value);
                        }}
                    />
                </div>

                {groupAlphabetically && onGroupingChange && (
                    <div className="scrollbar-hidden w-[85dvw] overflow-x-scroll md:w-unset md:max-w-[320px]">
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                className={cn(
                                    'min-w-4 text-center text-gray-500 decoration-gray-900 decoration-2 underline-offset-4',
                                    !groupedBy
                                        ? 'text-gray-900 underline'
                                        : undefined,
                                )}
                                onClick={() => onGroupingChange()}
                            >
                                All
                            </button>

                            {Object.entries(FilterGroupOptions).map(
                                ([key, label]) => {
                                    const k = key as keyof FilterGroupOptions;

                                    return (firstCharacterGroups?.[k] ?? 0) >
                                        0 ? (
                                        <button
                                            key={k}
                                            className={cn(
                                                'text-center text-gray-500 decoration-gray-900 decoration-2 underline-offset-4',
                                                groupedBy && groupedBy === k
                                                    ? 'text-gray-900 underline'
                                                    : undefined,
                                                k === 'numeric'
                                                    ? 'min-w-6'
                                                    : 'min-w-3',
                                            )}
                                            onClick={() => onGroupingChange(k)}
                                        >
                                            {label}
                                        </button>
                                    ) : (
                                        <div
                                            key={k}
                                            className={cn(
                                                'min-w-3 cursor-not-allowed text-center text-gray-500/30',
                                                k === 'numeric'
                                                    ? 'min-w-6'
                                                    : 'min-w-3',
                                            )}
                                        >
                                            {label}
                                        </div>
                                    );
                                },
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-6">{children}</div>
        </div>
    );
};
